import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { VendorStatusPill, DocStatusBadge } from '@/components/VendorStatusPill';
import { getDocuments, uploadDocument, triggerExtraction } from '@/lib/api';
import api from '@/lib/api';
import { Search, Upload, FileText, ShieldCheck, Loader2, Eye, ChevronRight, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { UpgradeDialog } from '@/components/UpgradeDialog';

export default function DocumentsPage() {
  const [docs, setDocs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadVendorId, setUploadVendorId] = useState('');
  const [uploadDocType, setUploadDocType] = useState('auto');
  const [selectedFile, setSelectedFile] = useState(null);
  const [extracting, setExtracting] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [upgrade, setUpgrade] = useState({ open: false, message: '' });
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  // Helper to check if document is expired
  const isDocExpired = (doc) => {
    if (!doc.expires_at) return false;
    const now = new Date();
    const expDate = new Date(doc.expires_at);
    return expDate < now;
  };

  // Get proper document status - ALWAYS check expiration first
  const getDocStatus = (doc) => {
    if (isDocExpired(doc)) return 'expired';
    
    if (doc.document_state === 'valid') return 'compliant';
    if (doc.document_state === 'expired') return 'expired';
    if (doc.document_state === 'invalid') return 'expired';
    if (doc.document_state === 'pending_review') return 'warning';
    
    if (doc.extraction_status === 'approved') return 'compliant';
    if (doc.extraction_status === 'completed') return 'warning';
    if (doc.extraction_status === 'failed') return 'expired';
    
    if (doc.status === 'completed') return 'warning';
    if (doc.status === 'failed') return 'expired';
    
    return 'pending';
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (typeFilter !== 'all') params.document_type = typeFilter;
      const { data } = await getDocuments(params);
      setDocs(data.items || []);
      setTotal(data.total || 0);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [statusFilter, typeFilter]);

  const handleUpload = async () => {
    if (!selectedFile || !uploadVendorId) {
      toast.error('Please select a file and enter vendor ID');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('vendor_id', uploadVendorId);
      formData.append('document_type', uploadDocType);
      const { data } = await uploadDocument(formData);
      toast.success(`Document uploaded! AI extraction in progress...`);
      setShowUpload(false);
      setSelectedFile(null);
      setUploadVendorId('');
      setUploadDocType('auto');
      // Navigate to detail
      navigate(`/documents/${data.id}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : detail?.message;
      // Limited Demo 5-vendor-with-docs cap → show upgrade popup
      if (msg && msg.includes('Maximum 5 vendors with documents')) {
        setShowUpload(false);
        setUpgrade({ open: true, message: msg });
      } else {
        toast.error(msg || 'Upload failed');
      }
    } finally { setUploading(false); }
  };

  const handleExtract = async (docId) => {
    setExtracting(docId);
    try {
      await triggerExtraction(docId);
      toast.success('Extraction triggered');
      setTimeout(() => load(), 2000);
    } catch { toast.error('Extraction failed'); }
    finally { setExtracting(null); }
  };

  const handleDelete = async (docId, filename) => {
    if (!window.confirm(`Delete "${filename}"? This will permanently remove the document and all extracted data. This action cannot be undone.`)) return;
    
    setDeleting(docId);
    try {
      await api.delete(`/documents/${docId}`);
      toast.success('Document deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete document');
    } finally {
      setDeleting(null);
    }
  };

  const statusMap = {
    completed: 'compliant', approved: 'compliant', failed: 'expired',
    review_required: 'warning', uploaded: 'pending', queued: 'pending',
    extracting: 'info', processing: 'info'
  };

  return (
    <div data-testid="documents-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Documents</h1>
          <p className="text-sm text-muted-foreground">{total} documents</p>
        </div>
        <Button data-testid="documents-upload-button" onClick={() => setShowUpload(true)}>
          <Upload className="mr-2 h-4 w-4" /> Upload Document
        </Button>
      </div>

      <Card className="rounded-xl border shadow-[var(--shadow-soft)]">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px]"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="review_required">Review Required</SelectItem>
                <SelectItem value="queued">Queued</SelectItem>
                <SelectItem value="processing">Processing</SelectItem>
                <SelectItem value="uploaded">Uploaded</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[160px]"><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="coi">COI</SelectItem>
                <SelectItem value="w9">W-9</SelectItem>
                <SelectItem value="license">License</SelectItem>
                <SelectItem value="workers_comp">Workers Comp</SelectItem>
                <SelectItem value="auto_insurance">Auto Insurance</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-xl border shadow-[var(--shadow-soft)]">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-12 rounded" />)}</div>
          ) : docs.filter(d => !d.archived).length === 0 ? (
            <div className="py-16 text-center">
              <FileText className="mx-auto h-12 w-12 text-muted-foreground/40 mb-4" />
              <p className="text-muted-foreground">No active documents found</p>
              <Button variant="outline" className="mt-4" onClick={() => setShowUpload(true)}>Upload your first document</Button>
            </div>
          ) : (
            <Table data-testid="documents-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Filename</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Confidence</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead className="w-24"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {docs.filter(d => !d.archived).map(doc => (
                  <TableRow key={doc.id} className="cursor-pointer hover:bg-muted/50 transition-colors duration-150" onClick={() => navigate(`/documents/${doc.id}`)}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                        <span className="text-sm truncate max-w-[200px]">{doc.filename}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{doc.vendor_name}</TableCell>
                    <TableCell><Badge variant="secondary" className="text-xs uppercase">{doc.document_type}</Badge></TableCell>
                    <TableCell>
                      <DocStatusBadge status={isDocExpired(doc) ? 'expired' : (doc.document_state || doc.extraction_status || doc.status || 'uploaded')} />
                    </TableCell>
                    <TableCell className="text-sm tabular-nums">{doc.ai_confidence ? `${(doc.ai_confidence * 100).toFixed(0)}%` : '-'}</TableCell>
                    <TableCell className="text-sm">{doc.expires_at ? new Date(doc.expires_at).toLocaleDateString() : '-'}</TableCell>
                    <TableCell>
                      <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                        {(doc.status !== 'completed' && doc.extraction_status !== 'completed' && doc.extraction_status !== 'approved') && (
                          <Button variant="ghost" size="sm" disabled={extracting === doc.id} onClick={() => handleExtract(doc.id)}>
                            {extracting === doc.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldCheck className="h-3 w-3" />}
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/documents/${doc.id}`)}>
                          <Eye className="h-3 w-3" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          disabled={deleting === doc.id}
                          onClick={() => handleDelete(doc.id, doc.filename)}
                          data-testid={`delete-doc-${doc.id}`}
                        >
                          {deleting === doc.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3 text-destructive" />}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Archived Documents */}
      {docs.filter(d => d.archived).length > 0 && (
        <Card className="rounded-xl border-dashed shadow-[var(--shadow-soft)]">
          <CardHeader className="pb-2">
            <div>
              <h3 className="text-sm font-medium text-muted-foreground" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Archive ({docs.filter(d => d.archived).length} documents)
              </h3>
              <p className="text-xs text-muted-foreground mt-1">Old document versions - not used for compliance</p>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Filename</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Archived</TableHead>
                  <TableHead className="w-24"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {docs.filter(d => d.archived).map(doc => (
                  <TableRow key={doc.id} className="opacity-60 cursor-pointer hover:bg-muted/30" onClick={() => navigate(`/documents/${doc.id}`)}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                        <span className="text-sm truncate max-w-[200px]">{doc.filename}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{doc.vendor_name}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs uppercase">{doc.document_type}</Badge></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{doc.archived_at ? new Date(doc.archived_at).toLocaleDateString() : '-'}</TableCell>
                    <TableCell onClick={e => e.stopPropagation()}>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        disabled={deleting === doc.id}
                        onClick={() => handleDelete(doc.id, doc.filename)}
                      >
                        {deleting === doc.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3 text-muted-foreground" />}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Upload Dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent>
          <DialogHeader><DialogTitle style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Upload Document</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div data-testid="document-ingestion-dropzone"
              className="border-2 border-dashed border-border rounded-xl p-6 text-center hover:border-primary/50 transition-colors duration-200 cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
              onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('border-primary'); }}
              onDragLeave={e => { e.currentTarget.classList.remove('border-primary'); }}
              onDrop={e => { e.preventDefault(); e.currentTarget.classList.remove('border-primary'); const f = e.dataTransfer.files[0]; if (f) setSelectedFile(f); }}
            >
              <input ref={fileInputRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.tiff" className="hidden" onChange={e => setSelectedFile(e.target.files[0])} />
              {selectedFile ? (
                <div className="flex items-center gap-3 justify-center">
                  <FileText className="h-8 w-8 text-primary" />
                  <div className="text-left">
                    <p className="text-sm font-medium">{selectedFile.name}</p>
                    <p className="text-xs text-muted-foreground">{(selectedFile.size / 1024).toFixed(0)} KB</p>
                  </div>
                </div>
              ) : (
                <>
                  <Upload className="mx-auto h-8 w-8 text-muted-foreground/40 mb-2" />
                  <p className="text-sm text-muted-foreground">Click or drag & drop to upload</p>
                  <p className="text-xs text-muted-foreground mt-1">PDF, PNG, JPEG, TIFF up to 25MB</p>
                </>
              )}
            </div>
            <div>
              <Label>Vendor ID *</Label>
              <Input data-testid="upload-vendor-id-input" placeholder="e.g. vnd-001" value={uploadVendorId} onChange={e => setUploadVendorId(e.target.value)} />
            </div>
            <div>
              <Label>Document Type</Label>
              <Select value={uploadDocType} onValueChange={setUploadDocType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto-detect</SelectItem>
                  {['coi','w9','license','workers_comp','auto_insurance','umbrella','bond','other'].map(t =>
                    <SelectItem key={t} value={t}>{t.toUpperCase().replace('_', ' ')}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpload(false)}>Cancel</Button>
            <Button data-testid="upload-submit-button" onClick={handleUpload} disabled={uploading || !selectedFile || !uploadVendorId}>
              {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
              {uploading ? 'Uploading...' : 'Upload & Analyze'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UpgradeDialog
        open={upgrade.open}
        onClose={() => setUpgrade({ open: false, message: '' })}
        title="Demo Limit Reached"
        message={upgrade.message}
      />
    </div>
  );
}
